import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Sparkles, MapPin, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../mock';

const CasaSection = () => {
  const { t } = useLanguage();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const icons = [Sparkles, MapPin, Heart];

  return (
    <section id="casa" ref={ref} className="relative py-32 bg-[#F5F1E8]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-9xl font-serif text-[#C9826B]/10 absolute top-10 left-6"
        >
          01
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="relative z-10"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif text-[#2A2520] mb-4">
              {t.casa.title}
            </h2>
            <p className="text-xl text-[#C9826B] tracking-wider uppercase">
              {t.casa.subtitle}
            </p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            {/* Text Content */}
            <motion.div variants={itemVariants} className="space-y-6">
              <p className="text-lg text-[#2A2520]/80 leading-relaxed">
                {t.casa.description}
              </p>

              {/* Features */}
              <div className="space-y-6 pt-8">
                {t.casa.features.map((feature, index) => {
                  const Icon = icons[index];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.5 + index * 0.2, duration: 0.6 }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C9826B]/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#C9826B]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-[#2A2520] mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-[#2A2520]/70">{feature.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Images */}
            <motion.div variants={itemVariants} className="space-y-6">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden shadow-2xl bg-[#2A2520]"
              >
                <img
                  src={images.casa1}
                  alt="Ca' del Sole Italia interior"
                  className="w-full h-auto block"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2520]/30 to-transparent pointer-events-none" />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden shadow-2xl bg-[#2A2520]"
              >
                <img
                  src={images.casa2}
                  alt="Ca' del Sole Italia details"
                  className="w-full h-auto block"
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Decorative Line */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9826B] to-transparent" />
    </section>
  );
};

export default CasaSection;
